import { useState, useCallback, useRef } from "react";
import { Download, Upload } from "lucide-react";
import { exportStorageData, importStorageData } from "@/lib/db";
import { useI18n } from "@/lib/i18n";
import ProcessStatusDialog from "@/components/shared/ProcessStatusDialog";
import ConfirmDialog from "@/components/shared/ConfirmDialog";
import { toast } from "sonner";

const ExportImportButtons = () => {
  const { t } = useI18n();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmType, setConfirmType] = useState<'export' | 'import'>('export');
  
  const [status, setStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [step, setStep] = useState("");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const startExport = async () => {
    try {
      setDialogOpen(true);
      setStatus('processing');
      setError("");
      
      const blob = await exportStorageData((s, p) => {
        setStep(s);
        setProgress(p);
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const date = new Date().toISOString().split('T')[0];
      a.href = url;
      a.download = `my-favorite-copies-backup-${date}.mfc`;
      a.click();
      URL.revokeObjectURL(url);

      setStatus('success');
      setProgress(100);
      setStep('done');
      toast.success(t('storage.export_success') || '백업 파일이 생성되었습니다.');
    } catch (err) {
      console.error(err);
      setStatus('error');
      setError(err instanceof Error ? err.message : String(err));
      toast.error(t('storage.export_failed') || '내보내기 실패');
    }
  };

  const startImport = async (file: File) => {
    try {
      setDialogOpen(true);
      setStatus('processing');
      setError("");
      
      await importStorageData(file, (s, p) => {
        setStep(s);
        setProgress(p);
      });

      setStatus('success');
      setProgress(100);
      setStep('done');
      toast.success(t('storage.import_success') || '데이터를 복구했습니다.');
      
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error(err);
      setStatus('error');
      setError(err instanceof Error ? err.message : String(err));
      toast.error(t('storage.import_failed') || '가져오기 실패');
    }
  };

  const handleExportClick = () => {
    setConfirmType('export');
    setConfirmOpen(true);
  };

  const handleImportClick = () => {
    setConfirmType('import');
    setConfirmOpen(true);
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      startImport(file);
    }
    e.target.value = "";
  };

  return (
    <div className="flex items-stretch gap-2 min-w-0 h-full">
      <button
        onClick={handleExportClick}
        data-testid="export-button"
        className="flex-1 flex items-center justify-center gap-2 px-3 rounded-xl glass-bar border border-primary/20 hover:bg-primary/10 transition-all text-[11px] font-bold text-primary group min-w-0"
      >
        <span className="truncate">{t('storage.export')}</span>
        <Upload className="w-3.5 h-3.5 shrink-0 group-hover:translate-x-[3px] transition-transform" />
      </button>
      
      <button
        onClick={handleImportClick}
        data-testid="import-button"
        className="flex-1 flex items-center justify-center gap-2 px-3 rounded-xl glass-bar border border-primary/20 hover:bg-primary/10 transition-all text-[11px] font-bold text-primary group min-w-0"
      >
        <span className="truncate">{t('storage.import')}</span>
        <Download className="w-3.5 h-3.5 shrink-0 group-hover:translate-x-[3px] transition-transform" />
      </button>

      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={onFileChange} 
        accept=".mfc" 
        className="hidden" 
      />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={confirmType === 'export' ? t('storage.export') : t('storage.import')}
        description={confirmType === 'export' ? t('storage.export_confirm') : t('storage.import_confirm')}
        onConfirm={() => {
          if (confirmType === 'export') {
            startExport();
          } else {
            fileInputRef.current?.click();
          }
        }}
        variant={confirmType === 'import' ? 'destructive' : 'default'}
      />

      <ProcessStatusDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        status={status}
        step={step}
        progress={progress}
        error={error}
      />
    </div>
  );
};

export default ExportImportButtons;
